import { FunctionComponent, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { RatesList } from './RatesList/RatesList';
import RatesIsEmpty from './RatesIsEmpty';
import { useBoardingRates } from '../BoardingRatesProvider';
import { Spinner } from 'components/UI';
import styles from './RatesContent.module.scss';

interface RatesContentProps {
    onAdd: () => void;
}

const RatesContent: FunctionComponent<RatesContentProps> = ({ onAdd }) => {
    const {
        isLoading,
        isFirstRender,
        areServicesLoading,
        isFilteredEmpty,
        isTrueEmpty,
        selectedFilter,
        boardingServices,
        allServiceRates,
        filterChangeHandler
    } = useBoardingRates();

    const filteredBoardingServices = useMemo(() => {
        const now = moment();
        return boardingServices.filter(service =>
            allServiceRates.some(sr => {
                if (sr.rateItem.itemId !== service.id) return false;
                const isExpired = now.isAfter(sr.rate.endDate);
                return selectedFilter === 'active' ? sr.rate.active && !isExpired : !sr.rate.active || isExpired;
            })
        );
    }, [boardingServices, allServiceRates, selectedFilter]);

    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const handleStatusChange = (option: string) => {
        filterChangeHandler(option);
    };

    const renderContent = () => {
        // Show full-screen spinner for initial load or while services are loading
        if (isFirstRender || areServicesLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        // True empty state - no rates at all (on default "active" filter)
        if (isTrueEmpty) {
            return (
                <RatesIsEmpty
                    title="Rates"
                    text='Click the "Add Rate" button to add your first one'
                    showButton={true}
                    onClick={onAdd}
                />
            );
        }

        // Filtered empty or has rates - always show header with filters
        return (
            <>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={
                            <SelectionBar
                                options={[
                                    { id: 'active', name: 'Active' },
                                    { id: 'inactive', name: 'Inactive' }
                                ]}
                                selectedId={selectedFilter}
                                onSelect={handleStatusChange}
                                className={styles.selectionBar}
                            />
                        }
                        buttonLabel={isMobile ? 'Add' : 'Add Rate'}
                        displayButton={true}
                        onClick={onAdd}
                        buttonIcon={faPlus}
                        className={styles.header}
                    />
                </div>

                <div className={styles.contentContainer}>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner />
                        </div>
                    ) : isFilteredEmpty ? (
                        <RatesIsEmpty
                            title="No rates found"
                            text="No Rates match your filter selection. Try changing or clearing the filter to see more."
                            showButton={false}
                        />
                    ) : (
                        <RatesList boardingServices={filteredBoardingServices} />
                    )}
                </div>
            </>
        );
    };

    return <div className={styles.root}>{renderContent()}</div>;
};

export default RatesContent;
